"use client";
import Link from "next/link";
import { FaTiktok, FaInstagram, FaPhone, FaEnvelope, FaLocationDot } from "react-icons/fa6";
import MenuItems from "./nav/MenuItems";

import { openCookieSettings } from "./cookie/CookieBanner";
import MapEmbed from "./MapEmbad";

const BOOKING_URL = "/werkstatt-service";
const MAPS_URL =
    "https://www.google.com/maps/search/?api=1&query=Badenheimer+Str.+4,+55576+Sprendlingen,+Deutschland";

const Footer = () => {
    const menuItems = MenuItems();
    const pageLinks = menuItems.filter((item) => item.main);
    const legalLinks = menuItems.filter((item) => item.legal);
    const year = new Date().getFullYear();

    return (
        <footer className="border-t border-ao-line bg-ao-bg-deep text-ao-fg">
            <div className="mx-auto max-w-[1440px] px-5 lg:px-20">

                {/* Aufruf zum Termin */}
                <div className="flex flex-col gap-6 border-b border-ao-line py-12 lg:flex-row lg:items-center lg:justify-between lg:py-16">
                    <div className="flex flex-col gap-3">
                        <span className="text-xs font-semibold tracking-[0.18em] text-ao-accent-fg lg:text-[13px]">
                            BEREIT FÜR IHREN TERMIN?
                        </span>
                        <h2 className="font-display text-4xl font-extrabold uppercase leading-none lg:text-6xl">
                            Wir kümmern uns um Ihr Auto
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 gap-3 sm:flex">
                        <Link
                            href={BOOKING_URL}
                            className="flex h-[54px] items-center justify-center rounded-xl bg-ao-accent px-7 text-base font-bold text-ao-on-accent transition-colors hover:bg-ao-accent-hover"
                        >
                            Termin buchen
                        </Link>
                        <a
                            href="tel:+4915158124394"
                            className="flex h-[54px] items-center justify-center gap-2 rounded-xl border border-ao-line-strong px-7 text-base font-semibold transition-colors hover:border-ao-accent"
                        >
                            <FaPhone className="text-sm text-ao-accent-fg" />
                            Anrufen
                        </a>
                    </div>
                </div>

                {/* Spalten */}
                <div className="grid grid-cols-1 gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:gap-12 lg:py-16">

                    {/* Marke */}
                    <div className="flex flex-col gap-5">
                        <Link href="/" className="flex items-center gap-3">
                            <img src="/logo.png" alt="A und O Autoservice" className="h-12 w-12 rounded-xl object-cover" />
                            <span className="flex flex-col leading-none">
                                <span className="font-display text-2xl font-extrabold tracking-[0.04em]">A UND O</span>
                                <span className="mt-1 text-[11px] font-semibold tracking-[0.22em] text-ao-muted">AUTOSERVICE</span>
                            </span>
                        </Link>
                        <p className="max-w-[260px] text-[15px] leading-relaxed text-ao-muted">
                            Service, An- und Verkauf – alles bei einem Ansprechpartner.
                        </p>
                        <div className="flex gap-3">
                            <a
                                href="https://www.instagram.com/aundoauto"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="A und O auf Instagram"
                                className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-ao-line-strong text-lg transition-colors hover:border-ao-accent hover:text-ao-accent-fg"
                            >
                                <FaInstagram />
                            </a>
                            <a
                                href="https://www.tiktok.com/@hazaraausdaykundi"
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="A und O auf TikTok"
                                className="flex h-11 w-11 items-center justify-center rounded-[10px] border border-ao-line-strong text-lg transition-colors hover:border-ao-accent hover:text-ao-accent-fg"
                            >
                                <FaTiktok />
                            </a>
                        </div>
                    </div>

                    {/* Kontakt */}
                    <div className="flex flex-col gap-4">
                        <h3 className="font-display text-xl font-bold uppercase tracking-wide">Kontakt</h3>
                        <a href="tel:+4915158124394" className="flex items-center gap-3 text-[15px] text-ao-soft transition-colors hover:text-ao-fg">
                            <FaPhone className="shrink-0 text-ao-accent-fg" />
                            +49 151 58124394
                        </a>
                        <a href="tel:+4967014499835" className="flex items-center gap-3 text-[15px] text-ao-soft transition-colors hover:text-ao-fg">
                            <FaPhone className="shrink-0 text-ao-accent-fg" />
                            +49 6701 4499835
                        </a>
                        <a href="mailto:autoservice.aundo@gmail.com" className="flex items-center gap-3 break-all text-[15px] text-ao-soft transition-colors hover:text-ao-fg">
                            <FaEnvelope className="shrink-0 text-ao-accent-fg" />
                            autoservice.aundo@gmail.com
                        </a>
                    </div>

                    {/* Adresse */}
                    <div className="flex flex-col gap-4">
                        <h3 className="font-display text-xl font-bold uppercase tracking-wide">Adresse</h3>
                        <a
                            href={MAPS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex gap-3 text-[15px] leading-relaxed text-ao-soft transition-colors hover:text-ao-fg"
                        >
                            <FaLocationDot className="mt-1 shrink-0 text-ao-accent-fg" />
                            <span>
                                Badenheimer Str. 4<br />
                                55576 Sprendlingen<br />
                                Deutschland
                            </span>
                        </a>
                        <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" className="text-[15px] font-bold text-ao-accent-fg">
                            Route planen →
                        </a>
                    </div>

                    {/* Seiten */}
                    <div className="flex flex-col gap-4">
                        <h3 className="font-display text-xl font-bold uppercase tracking-wide">Seiten</h3>
                        <ul className="flex flex-col gap-3">
                            {pageLinks.map((item) => (
                                <li key={item.url}>
                                    <Link href={item.url} className="text-[15px] text-ao-soft transition-colors hover:text-ao-fg">
                                        {item.title}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Karte – lädt erst nach Zustimmung */}
                <MapEmbed />

                {/* Unterste Zeile */}
                <div className="flex flex-col gap-3 py-8 text-sm text-ao-muted md:flex-row md:items-center md:justify-between">
                    <p>© 2024 A und O Autoservice. Alle Rechte vorbehalten.</p>
                    <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                        {legalLinks.map((item) => (
                            <Link key={item.url} href={item.url} className="transition-colors hover:text-ao-fg">
                                {item.title}
                            </Link>
                        ))}
                        <button type="button" onClick={openCookieSettings} className="transition-colors hover:text-ao-fg">
                            Cookie-Einstellungen
                        </button>
                        <span>Powered by Ghulam Karimi</span>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
